import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { mutateAsync, requestAsync } from 'redux-query';
import { DateTime, Settings } from 'luxon';
import { Redirect, RouteComponentProps } from '@reach/router';
import { Button2, Text2, Stack } from '@el8/vital';
import ChartSelection from 'views/account/ChartSelection';
import ButtonsBlock from 'views/booking/common/ButtonsBlock/ButtonsBlock';
import Content from 'views/booking/common/Content/Content';
import AppointmentSummary from 'views/booking/AppointmentSummary/AppointmentSummary';
import { getBookingInfo, confirmAppointmentQuery, clearBookingInfo } from 'modules/booking';
import { getActivePatient, logout as logoutAction, getCurrentPatient } from 'modules/patient';
import { getPractice } from 'modules/practice';
import { notify } from 'utils/notification';
import { logEvent, getStartTime } from 'utils/amplitude';
import { isPatientEmpty } from 'utils/patient';
import {
  State as ReduxState,
  PracticeInfo,
  LuxonFormats,
  ConfirmAppointmentBody,
  RQConfirmAppointmentResponse,
  HydratedPatientInfo,
  SessionState,
  BookingState,
  PatientChartInfo,
  ConfirmAppointmentSuccessResponse,
  Response,
  Dispatch,
  RQResponse,
  PatientRaw,
} from 'types';
import { updateSession } from 'modules/session';
import { checkResponse } from 'utils/errors';
import styles from './Confirmation.less';

type OwnProps = {
  practiceId: number;
} & RouteComponentProps;

type StateProps = {
  bookingInfo: BookingState;
  patient: HydratedPatientInfo;
  practice: PracticeInfo;
  session: SessionState;
};

type DispatchProps = {
  clearBookingState: () => void;
  confirmAppointment: (
    appointmentData: ConfirmAppointmentBody,
  ) => Promise<RQConfirmAppointmentResponse>;
  logout: () => void;
  fetchCurrentPatient: () => Promise<RQResponse<Response<PatientRaw>>>;
  selectPatientChart: (chartId: number) => void;
};

type Props = {
  confirmAppointment: () => void;
} & OwnProps &
  StateProps &
  DispatchProps;

type State = {
  isPending: boolean;
};

const getAppointmentData = (
  practiceId: number,
  bookingInfo: BookingState,
  selectedChart: PatientChartInfo,
): ConfirmAppointmentBody => {
  const { appointmentTypeId, patient, physicianId, scheduledDate, selectedServiceLocationId } =
    bookingInfo;
  let patientData;
  if (selectedChart && selectedChart.belongsToPractice) {
    patientData = { id: selectedChart.id };
  } else if (selectedChart && !selectedChart.belongsToPractice) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, dob, ...rest } = selectedChart;
    patientData = {
      ...rest,
      dob: DateTime.fromFormat(dob, LuxonFormats.UserFacingDob).toISODate(),
    };
  } else {
    patientData = {
      ...patient,
      dob: DateTime.fromFormat(patient.dob, LuxonFormats.UserFacingDob).toISODate(),
    };
  }
  const appointmentData = {
    appointment: {
      appointmentTypeId,
      scheduledDate: DateTime.fromISO(scheduledDate, { zone: Settings.defaultZone }).toISO(),
    },
    patient: patientData,
    physicianId,
    practiceId: Number(practiceId),
    serviceLocationId: selectedServiceLocationId,
  };
  return appointmentData;
};

const mapStateToProps = (state: ReduxState, props: OwnProps): StateProps => ({
  bookingInfo: getBookingInfo(state),
  patient: getActivePatient(state),
  practice: getPractice(state, props),
  session: state.session,
});

const mapDispatchToProps = (dispatch: Dispatch, props: OwnProps): DispatchProps => ({
  clearBookingState: (): void => {
    dispatch(clearBookingInfo());
  },
  confirmAppointment: async (
    appointmentData: ConfirmAppointmentBody,
  ): Promise<RQConfirmAppointmentResponse> =>
    dispatch(mutateAsync(confirmAppointmentQuery(appointmentData))),
  fetchCurrentPatient: (): Promise<RQResponse<Response<PatientRaw>>> =>
    dispatch(requestAsync(getCurrentPatient(props.practiceId))),
  logout(): void {
    dispatch(logoutAction());
  },
  selectPatientChart: (chartId): void => {
    dispatch(updateSession({ activeChartId: chartId }));
  },
});

class Confirmation extends PureComponent<Props, State> {
  state = { isPending: false };

  handleBackClick = (): void => {
    logEvent('patient clicks back on confirm');
    window.history.back();
  };

  handleConfirmClick = (): void => {
    const {
      bookingInfo,
      clearBookingState,
      confirmAppointment,
      fetchCurrentPatient,
      logout,
      navigate,
      patient,
      practiceId,
      selectPatientChart,
      session,
    } = this.props;
    const selectedChart = patient
      ? patient.charts.find((chart): boolean => chart.id === session.activeChartId)
      : undefined;
    const appointmentData = getAppointmentData(practiceId, bookingInfo, selectedChart);

    this.setState({ isPending: true }, async (): Promise<void> => {
      try {
        const bookAppointmentResponse = await confirmAppointment(appointmentData);
        checkResponse(bookAppointmentResponse);
        /**
         * Special handling for the edge case
         * If passport user books an appointment for the chart that does not belong
         * to the current practice (the practice they are booking an appointment with)
         * on the backend, we create a new chart within the current practice that has
         * the same demographic as the original chart.
         * After the appointment was booked, we want to fetch that newly created chart
         * and replace the original chart with it.
         */
        if (selectedChart && !selectedChart.belongsToPractice) {
          try {
            const currentPatientResponse = await fetchCurrentPatient();
            checkResponse(currentPatientResponse);
            selectPatientChart(
              (bookAppointmentResponse.body as ConfirmAppointmentSuccessResponse).data.patient.id,
            );
          } catch {
            // In case there is something wrong with the fetching current patient
            // we want to logout, to avoid potential broken state when `activeUserId`
            // in `session` reducer points to the wrong chart
            logout();
          }
        }

        await navigate('../success', {
          state: {
            appointmentId: (bookAppointmentResponse.body as ConfirmAppointmentSuccessResponse).data
              .appointment.id,
          },
        });
        clearBookingState();
      } catch (error) {
        notify(error.message);
      }
      this.setState({ isPending: false });
    });

    const startTime = getStartTime();
    const now = DateTime.fromObject({ zone: Settings.defaultZone });
    const { hours } = DateTime.fromISO(bookingInfo.scheduledDate).diff(startTime, ['hours']);
    const { minutes } = now.diff(startTime, ['minutes']);

    logEvent('appointment confirmed', {
      'hours in advance': hours.toFixed(2),
      'time to completion': minutes.toFixed(2),
    });
  };

  render(): JSX.Element {
    const { bookingInfo, patient, practiceId, practice, navigate } = this.props;

    if (!(bookingInfo.appointmentTypeId && bookingInfo.physicianId && bookingInfo.scheduledDate)) {
      return <Redirect noThrow to={`book/${practiceId}/`} />;
    }
    const bookViaEmailOrPhone = window.el8Globals.FEATURES.BookViaEmailOrPhone;
    if (!patient && isPatientEmpty(bookingInfo.patient, bookViaEmailOrPhone)) {
      return <Redirect noThrow to={`book/${practiceId}/account`} />;
    }

    return (
      <Content>
        <div className={styles.header}>
          <Stack gap="2xs">
            <div>
              <Button2 kind="link" onClick={this.handleBackClick} label="Back" icon="back" />
            </div>
            <div>
              {!isPatientEmpty(bookingInfo.patient, bookViaEmailOrPhone) && (
                <Text2 align="center" appearance="system" tag="p">
                  Review and confirm appointment for{' '}
                  <strong>{`${bookingInfo.patient.firstName} ${bookingInfo.patient.lastName}`}</strong>
                </Text2>
              )}
            </div>
          </Stack>
        </div>
        <AppointmentSummary practiceId={practiceId} />
        {patient && (
          <ChartSelection
            addNewPatient={(): void => navigate('../add-patient')}
            title="Who is the appointment for?"
          />
        )}
        <Text2 align="center" className={styles.cancellation} tag="div">
          {practice.settings.cancellationPolicy}
        </Text2>
        <ButtonsBlock className={styles.floatingFooter}>
          <Button2
            busy={this.state.isPending}
            className={styles.nextButton}
            kind="primary"
            label="Confirm Appointment"
            onClick={this.handleConfirmClick}
          />
        </ButtonsBlock>
      </Content>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Confirmation);
